/**
 * Contact form enhancement: enables the server-rendered form, preselects `?topic=`, fills in the email
 * handed over by a "Schedule a demo" field, renders the Turnstile widget and sends the message as JSON to the
 * API without credentials. A demo request (`?topic=evaluation`) shows the short form: the topic choice folds
 * away behind "Choose another topic" and the message is optional. Plain DOM code: the public site ships no
 * framework runtime.
 */

/** Session key that carries a demo request's email from the demo field to the contact form (same tab). */
const demoEmailKey = 'hlt.demo-email';
const maxEmailLength = 254;

/**
 * The email to prefill and the query string without it. A handed-over value (session storage) wins over the
 * `email` query parameter of the no-JavaScript path; either way the address is removed from the URL so it does
 * not stay in history or travel with a copied link.
 */
export function takeDemoEmail(search: string, handedOver: string | null): { email: string | null; search: string } {
    const params = new URLSearchParams(search);
    const fromQuery = params.get('email');
    params.delete('email');
    const value = (handedOver ?? fromQuery ?? '').trim();
    const rest = params.toString();
    return {
        email: value && value.length <= maxEmailLength ? value : null,
        search: rest ? `?${rest}` : '',
    };
}

/**
 * Demo fields (`DemoForm`): after the browser's own validation passes, keep the address in this tab and open
 * the contact page without it in the URL. Without storage (or JavaScript) the plain GET navigation applies.
 */
export function enhanceDemoForms(): void {
    for (const form of document.querySelectorAll<HTMLFormElement>('form[data-demo-form]')) {
        form.addEventListener('submit', (event) => {
            const data = new FormData(form);
            const email = data.get('email');
            if (typeof email !== 'string' || !email.trim()) return;
            try {
                sessionStorage.setItem(demoEmailKey, email.trim());
            } catch {
                return;
            }
            event.preventDefault();
            const params = new URLSearchParams();
            for (const [name, value] of data)
                if (name !== 'email' && typeof value === 'string') params.append(name, value);
            const target = new URL(form.action);
            target.search = params.toString();
            window.location.assign(target.href);
        });
    }
}

function takeHandedOverEmail(): string | null {
    try {
        const value = sessionStorage.getItem(demoEmailKey);
        sessionStorage.removeItem(demoEmailKey);
        return value;
    } catch {
        return null;
    }
}

/**
 * The API requires a message of at least 20 characters. A demo request may leave it empty or short, so HLT's
 * (English) inbox receives this line, followed by whatever the visitor wrote.
 */
export const demoRequestNote = 'Demo request from the website.';

/** The message sent for a topic: a demo request never fails for a missing or short message. */
export function outgoingMessage(topic: string, message: string): string {
    if (topic !== 'evaluation' || message.length >= 20) return message;
    return message ? `${demoRequestNote}\n\n${message}` : demoRequestNote;
}

/** Token of Cloudflare's test site keys; a server without a Turnstile secret accepts only this one. */
const testToken = 'XXXX.DUMMY.TOKEN.XXXX';
const fields = ['name', 'email', 'organization', 'message', 'turnstileToken'] as const;
type FieldName = (typeof fields)[number];

interface Messages {
    summary: string;
    required: string;
    email: string;
    short: string;
    check: string;
    rateLimited: string;
    unavailable: string;
    failed: string;
}

interface TurnstileApi {
    render(container: HTMLElement, options: Record<string, unknown>): string;
    reset(widgetId: string): void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

function loadTurnstile(): Promise<TurnstileApi> {
    return new Promise((resolve, reject) => {
        if (window.turnstile) return resolve(window.turnstile);
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.addEventListener('load', () =>
            window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')),
        );
        script.addEventListener('error', () => reject(new Error('turnstile')));
        document.head.append(script);
    });
}

const isMessages = (value: unknown): value is Messages =>
    typeof value === 'object' && value !== null && 'summary' in value && 'failed' in value;

export function enhanceContactForm(): void {
    const form = document.querySelector<HTMLFormElement>('form[data-contact-form]');
    const success = document.querySelector<HTMLElement>('[data-contact-success]');
    const summary = form?.querySelector<HTMLElement>('[data-contact-summary]');
    const submit = form?.querySelector<HTMLButtonElement>('button[type="submit"]');
    const widget = form?.querySelector<HTMLElement>('[data-contact-turnstile]');
    if (!form) return;
    // The address leaves the URL even when the form itself is unavailable.
    const handOver = takeDemoEmail(window.location.search, takeHandedOverEmail());
    if (handOver.search !== window.location.search) {
        const { pathname, hash } = window.location;
        window.history.replaceState(window.history.state, '', `${pathname}${handOver.search}${hash}`);
    }
    if (form.hidden || !success || !summary || !submit || !widget) return;
    const parsed: unknown = JSON.parse(form.dataset.messages ?? '{}');
    if (!isMessages(parsed)) return;
    const messages = parsed;
    const siteKey = form.dataset.siteKey ?? '';

    const topic = new URLSearchParams(window.location.search).get('topic');
    const preset = topic
        ? form.querySelector<HTMLInputElement>(`input[name="topic"][value="${CSS.escape(topic)}"]`)
        : null;
    if (preset) preset.checked = true;

    const chooser = form.querySelector<HTMLFieldSetElement>('[data-topic-chooser]');
    const demoTopic = form.querySelector<HTMLElement>('[data-demo-topic]');
    if (topic === 'evaluation' && chooser && demoTopic) {
        chooser.hidden = true;
        demoTopic.hidden = false;
        demoTopic.querySelector('[data-change-topic]')?.addEventListener('click', () => {
            chooser.hidden = false;
            demoTopic.hidden = true;
            form.querySelector<HTMLInputElement>('input[name="topic"]:checked')?.focus();
        });
    }

    const hintsValue: unknown = JSON.parse(form.dataset.hints ?? '{}');
    const hints = typeof hintsValue === 'object' && hintsValue !== null ? (hintsValue as Record<string, unknown>) : {};
    const hint = document.getElementById('contact-message-hint');
    const selectedTopic = () => form.querySelector<HTMLInputElement>('input[name="topic"]:checked')?.value ?? 'other';
    const submitLabel = () =>
        (selectedTopic() === 'evaluation' ? submit.dataset.labelEvaluation : submit.dataset.labelOther) ?? '';
    const showTopic = () => {
        const current = selectedTopic();
        for (const group of form.querySelectorAll<HTMLElement>('[data-topic-details]'))
            group.hidden = group.dataset.topicDetails !== current;
        const text = hints[current];
        if (hint && typeof text === 'string') hint.textContent = text;
        for (const note of document.querySelectorAll<HTMLElement>('[data-topic-note]'))
            note.hidden = note.dataset.topicNote !== current;
        for (const marker of form.querySelectorAll<HTMLElement>('[data-optional-for]'))
            marker.hidden = marker.dataset.optionalFor !== current;
        control('message')?.toggleAttribute('required', current !== 'evaluation');
        submit.textContent = submitLabel();
    };
    const control = (name: FieldName) => form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${name}"]`);
    for (const radio of form.querySelectorAll<HTMLInputElement>('input[name="topic"]'))
        radio.addEventListener('change', showTopic);
    showTopic();

    let token = siteKey ? '' : testToken;
    let widgetId: string | null = null;
    if (siteKey) {
        loadTurnstile()
            .then((turnstile) => {
                widgetId = turnstile.render(widget, {
                    sitekey: siteKey,
                    action: 'contact',
                    language: form.dataset.locale ?? 'auto',
                    theme: 'auto',
                    callback: (value: string) => (token = value),
                    'expired-callback': () => (token = ''),
                    'error-callback': () => (token = ''),
                });
            })
            .catch(() => showSummary(messages.unavailable));
    }
    submit.disabled = false;

    const emailField = control('email');
    if (handOver.email && emailField && !emailField.value) {
        emailField.value = handOver.email;
        const next = (['name', 'message'] as const).map(control).find((field) => field && !field.value);
        next?.focus();
    }
    const setError = (name: FieldName, message: string | null) => {
        const slot = document.getElementById(`contact-${name}-error`);
        if (slot) {
            slot.textContent = message ?? '';
            slot.hidden = !message;
        }
        const input = control(name);
        if (message) input?.setAttribute('aria-invalid', 'true');
        else input?.removeAttribute('aria-invalid');
    };
    function showSummary(message: string | null) {
        if (!summary) return;
        summary.textContent = message ?? '';
        summary.hidden = !message;
        if (message) summary.focus();
    }

    const validate = (): FieldName | null => {
        let first: FieldName | null = null;
        const flag = (name: FieldName, message: string | null) => {
            setError(name, message);
            if (message && !first) first = name;
        };
        const value = (name: FieldName) => control(name)?.value.trim() ?? '';
        flag('name', value('name') ? null : messages.required);
        const email = control('email');
        flag('email', !value('email') ? messages.required : email?.validity.typeMismatch ? messages.email : null);
        const optionalMessage = selectedTopic() === 'evaluation';
        flag(
            'message',
            optionalMessage
                ? null
                : !value('message')
                  ? messages.required
                  : value('message').length < 20
                    ? messages.short
                    : null,
        );
        flag('turnstileToken', token ? null : messages.check);
        return first;
    };

    form.addEventListener('submit', (event) => {
        event.preventDefault();
        void send();
    });

    async function send() {
        if (!form || !submit) return;
        showSummary(null);
        const invalid = validate();
        if (invalid) {
            showSummary(messages.summary);
            control(invalid)?.focus();
            return;
        }
        const data = new FormData(form);
        const text = (name: string) => {
            const value = data.get(name);
            return typeof value === 'string' ? value.trim() : '';
        };
        const choice = (name: string) => text(name) || null;
        const topicValue = text('topic') || 'other';
        const body = {
            topic: topicValue,
            name: text('name'),
            email: text('email'),
            organization: text('organization') || null,
            message: outgoingMessage(topicValue, text('message')),
            locale: form.dataset.locale ?? 'en',
            // Hidden groups are sent too; the server keeps only the chosen topic's details.
            details: { area: choice('area') },
            turnstileToken: token,
        };
        submit.disabled = true;
        submit.textContent = submit.dataset.sending ?? submitLabel();
        try {
            const response = await fetch(form.dataset.endpoint ?? '/api/v1/contact-messages', {
                method: 'POST',
                credentials: 'omit',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (response.ok) {
                form.hidden = true;
                if (success) {
                    success.hidden = false;
                    success.focus();
                }
                return;
            }
            const failure = (await response.json().catch(() => null)) as {
                error?: { fieldErrors?: { path: string }[] };
            } | null;
            const paths = failure?.error?.fieldErrors?.map((f) => f.path) ?? [];
            if (paths.includes('turnstileToken')) setError('turnstileToken', messages.check);
            showSummary(
                response.status === 429
                    ? messages.rateLimited
                    : response.status === 503
                      ? messages.unavailable
                      : paths.length
                        ? messages.summary
                        : messages.failed,
            );
        } catch {
            showSummary(messages.failed);
        } finally {
            submit.disabled = false;
            submit.textContent = submitLabel();
            // Turnstile tokens are single-use: every attempt needs a fresh one.
            if (siteKey) {
                token = '';
                if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
            }
        }
    }
    summary.tabIndex = -1;
}
